/* eslint-disable @typescript-eslint/no-magic-numbers */
import { pcbEditorBehaviors } from "@buildwithflux/constants";
import { createDefaultHoles, createDefaultViaConfigurationsMap, DEFAULT_FOOTPRINT_STACKUP_KEY, DEFAULT_LAYOUT_STACKUP_KEY, getCopperLayersCount, isCopperLayer, LayoutRules, stackups, SystemPcbLayoutRulesValues, } from "@buildwithflux/models";
import { clampBetween } from "@buildwithflux/shared";
import { isUndefined, keyBy } from "lodash";
import { create } from "mutative";
import { isNumeric } from "../../../helpers/isNumeric";
import { isUserCreatedViaConfig, generateSubSampledConfigs } from "../../../smart_via/subSampling";
import { evaluateMechanicalValue } from "../../expression_engine/EvaluateExpression";
import { isExpression, normalizeEvaluatedExpression } from "../../expression_engine/ExpressionEvaluationHelpers";
import { LayerOrientation } from "../../rules";
import { pcbRuleImportantString } from "../constants";
// A pity to have to tell prettier to ignore the whole class, but that's the only
// way to get it not to completely mess up the parseValue method overloads.
// prettier-ignore
export class RuleParser {
    rules;
    lengthUnit;
    angleUnit;
    nodeType;
    expressionEvaluationContext;
    constructor(rules, expressionEvaluationContext, nodeType) {
        this.rules = rules;
        // Some rules are needed for parsing other rules, so we parse these separately first
        this.lengthUnit = RuleParser.parseValue("stringOption", rules.unit?.value ?? LayoutRules.unit.default);
        this.angleUnit = RuleParser.parseValue("stringOption", rules.rotationUnit?.value ?? LayoutRules.rotationUnit.default);
        this.nodeType = nodeType;
        this.expressionEvaluationContext = expressionEvaluationContext;
    }
    static stripImportantFlag(value) {
        const trimmed = value.trimEnd();
        if (trimmed.endsWith(pcbRuleImportantString)) {
            // If `trimmed` is `rectangle !important`, should return `rectangle`--
            // TODO: consider a regex for this instead
            return trimmed.substring(0, trimmed.length - pcbRuleImportantString.length).trimEnd();
        }
        else {
            return value;
        }
    }
    static parseValue(valueType, value, options) {
        return this[valueType](this.stripImportantFlag(value.toString()), options);
    }
    static booleanValue(value, options) {
        let normalizedValue = value.trim();
        if (isExpression(normalizedValue)) {
            const evaluatedValue = evaluateInputValue(normalizedValue, options.expressionEvaluationContext);
            if (evaluatedValue !== undefined) {
                normalizedValue = evaluatedValue.toString().trim();
            }
        }
        return normalizedValue.toLowerCase() === "true" || normalizedValue === "1";
    }
    static lengthValue(value, options) {
        if (value === SystemPcbLayoutRulesValues.auto) {
            return SystemPcbLayoutRulesValues.auto;
        }
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        // Note: NaN evaluatedValue case covered by clampValueFields(..)
        return (typeof evaluatedValue === 'number') ? clampValueFields(evaluatedValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static angle(value, options) {
        if (value === SystemPcbLayoutRulesValues.auto) {
            return SystemPcbLayoutRulesValues.auto;
        }
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.angleUnit, "rad", options.anglePrecision, options.validRange);
        // Note: NaN evaluatedValue case covered by clampValueFields(..)
        return (typeof evaluatedValue === 'number') ? clampValueFields(evaluatedValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static cornerValues(value, options) {
        let result = {};
        if (!isExpression(value)) {
            const values = splitStringComponentsByWhitespace(value).map((v) => evaluateInputValue(v, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange));
            // implements https://www.w3schools.com/css/css3_borders.asp
            switch (values.length) {
                case 1:
                    result = { topLeft: Number(values[0]), topRight: Number(values[0]), bottomRight: Number(values[0]), bottomLeft: Number(values[0]) };
                    break;
                case 2:
                    result = { topLeft: Number(values[0]), bottomRight: Number(values[0]), topRight: Number(values[1]), bottomLeft: Number(values[1]) };
                    break;
                case 3:
                    result = { topLeft: Number(values[0]), topRight: Number(values[1]), bottomLeft: Number(values[1]), bottomRight: Number(values[2]) };
                    break;
                case 4:
                    result = { topLeft: Number(values[0]), topRight: Number(values[1]), bottomRight: Number(values[2]), bottomLeft: Number(values[3]) };
                    break;
            }
        }
        else {
            const evaluatedValue = Number(evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange));
            result = { topLeft: evaluatedValue, topRight: evaluatedValue, bottomRight: evaluatedValue, bottomLeft: evaluatedValue };
        }
        const overrides = getOverrideFields(['topLeft', 'topRight', 'bottomRight', 'bottomLeft'], options.overwritingRuleFieldValues);
        return { ...result, ...overrides };
    }
    static lengthVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static angleVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, options.angleUnit, "rad", options.anglePrecision, options.validRange);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static scalar(value, options) {
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, undefined, undefined, undefined, options.validRange);
        // Note: NaN evaluatedValue case covered by clampValueFields(..)
        return (typeof evaluatedValue === 'number') ? clampValueFields(evaluatedValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static scalarVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, undefined, undefined, undefined, options.validRange, 1);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static text(value, options) {
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, undefined, options.lengthPrecision, options.validRange, true);
        return evaluatedValue?.toString();
    }
    static stringOption(value, _options) {
        return value;
    }
    static spaceSeparatedValues(value, _options) {
        return value.split(" ").map((item) => item?.trim());
    }
    static dynamicOption(value, _options) {
        return value;
    }
    static dynamicMultiOption(value, options) {
        const strings = value.trim().split(/\s*,\s*/);
        return strings.map(s => this.dynamicOption(s, options));
    }
    static boardStackup(value, options) {
        const hasLayout = Boolean(options.expressionEvaluationContext?.layout);
        return getStackupLayersFromValue(value, hasLayout, options.ruleData);
    }
    static selectorLengthsList(value, options) {
        if (value.trim().length === 0) {
            return undefined;
        }
        const evaluatedValue = evaluateSelectorLengthsList(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        // We cannot perform the clamping here :(
        return evaluatedValue;
    }
    parseRules(ruleNames) {
        /**
         * We declare it to be type of `any` here because this function is about
         * to construct the object using `ruleNames` as keys. In other words, as
         * long as `ruleNames` is passed correctly, the `parsedRules` object is
         * guaranteed to contain the expected fields.
         *
         * Plus, compiler wont allow us do const parsedRules: ParsedRulesFor<T> = {} here.
         *
         * TODO: Maybe we can handle this better?
         */
        const parsedRules = {};
        const parseOptions = {
            lengthUnit: this.lengthUnit,
            angleUnit: this.angleUnit,
            lengthPrecision: pcbEditorBehaviors.gridPositionInMetersDecimals,
            anglePrecision: pcbEditorBehaviors.gridPositionInRadDecimals,
            nodeType: this.nodeType,
            expressionEvaluationContext: this.expressionEvaluationContext,
        };
        for (const name of ruleNames) {
            const ruleConfig = LayoutRules[name];
            const options = { ...parseOptions };
            if (ruleConfig.overwritingRules) {
                const overwritingRules = this.parseRules(ruleConfig.overwritingRules);
                options.overwritingRuleFieldValues = ruleConfig.overwritingRules.map((key) => overwritingRules[key]);
            }
            if (ruleConfig.minRules) {
                const minFieldValues = this.parseRules(ruleConfig.minRules);
                options.minFieldValues = ruleConfig.minRules.map((key) => minFieldValues[key]);
            }
            if (ruleConfig.maxRules) {
                const maxFieldValues = this.parseRules(ruleConfig.maxRules);
                options.maxFieldValues = ruleConfig.maxRules.map((key) => maxFieldValues[key]);
            }
            options.validRange = ruleConfig.validRange;
            const rule = this.rules[name];
            options.ruleData = rule;
            let value = rule?.value;
            if (isUndefined(value) || value === "") {
                const defaultValue = ruleConfig.default;
                // If there's no defaultValue, lets fallback to the original `value` instead of undefined,
                // and let `RuleParser.parseValue` below to decide what the parsedValue is
                value = defaultValue === undefined ? value : defaultValue.toString();
            }
            else {
                value = value.toString();
            }
            let parsedValue = undefined;
            // If the user enters a wrong unit this can throw an exception and crash
            try {
                parsedValue = value === undefined ? value : RuleParser.parseValue(ruleConfig.valueType, value, options);
            }
            catch (e) {
                // TODO: Do something better with this error, like showing it to the user
                // For now we just use a default or empty value
                parsedValue = RuleParser.parseValue(ruleConfig.valueType, ruleConfig.default ?? "", options);
            }
            parsedRules[name] = parsedValue;
            // keep the context updated as rules get parsed to allow expressions to reference each other
            // Limitations: this only works if the dependencies got parsed before the dependent rule!
            if (this.expressionEvaluationContext?.this) {
                if (!this.expressionEvaluationContext.this.bakedRules) {
                    this.expressionEvaluationContext.this.bakedRules = {};
                }
                this.expressionEvaluationContext.this.bakedRules[name] = parsedValue;
            }
        }
        return parsedRules;
    }
}
// Spaces are separators, except inside double quotes
function splitStringComponentsByWhitespace(value) {
    const trimmed = value.trim();
    // First step: split by space and double quotes
    const groupsRegex = /(\\\\"|[^\s\n"])+|(?:"){2,}|"(?!")((\\\\"|[^"])*)"|"/g;
    const matches = [];
    let match = null;
    while ((match = groupsRegex.exec(trimmed))) {
        if (match[2]) {
            // Double quoted group
            matches.push({ index: match.index, value: match[2] });
        }
        else {
            // No quote group present
            matches.push({ index: match.index, value: match[0] });
        }
    }
    // This will split cases like abc"abc" into two tokens, but we want them to be joined
    // I don't know how to regex, so I'm going to use a reduce
    return matches.reduce((prev, next, index) => {
        if (index === 0)
            return [next.value];
        const last = prev[prev.length - 1];
        // Space is the only separator, so if we splitted and there was not a space, then it has to be joined
        if (trimmed[next.index - 1] !== " " && trimmed[next.index - 1] !== "\n") {
            return [...prev.slice(0, -1), last + next.value];
        }
        else {
            return [...prev, next.value];
        }
    }, []);
}
export function evaluateVector3(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, zFallbackValue = 0) {
    const trimmedValue = value.trim();
    if (trimmedValue === "" || trimmedValue === SystemPcbLayoutRulesValues.auto) {
        return { x: 0, y: 0, z: 0, autoX: true, autoY: true, autoZ: true };
    }
    if (!isExpression(trimmedValue)) {
        const values = splitStringComponentsByWhitespace(trimmedValue).map((componentValue) => {
            const result = evaluateInputValue(componentValue, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false);
            // If for example, the user specifies invalid expression for one or more vector components,
            // then result will be of string type. Preemptively catch, and resolve to expected number type,
            // in the best way we can:
            if (typeof result !== "number" || Number.isNaN(result)) {
                return validRange ? clampBetween(0, validRange[0], validRange[1]) : 0;
            }
            else {
                return result;
            }
        });
        return {
            x: values[0],
            y: values[values.length > 1 ? 1 : 0],
            z: values.length > 2 ? values[2] : values.length === 1 ? values[0] : zFallbackValue,
            autoX: false,
            autoY: false,
            autoZ: false,
        };
    }
    else {
        const evaluateValue = evaluateInputValue(trimmedValue, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false);
        return {
            x: Number(evaluateValue),
            y: Number(evaluateValue),
            z: Number(evaluateValue) || zFallbackValue,
            autoX: false,
            autoY: false,
            autoZ: false,
        };
    }
}
function getVectorOverrideFields(overwritingRuleFieldValues) {
    if (!overwritingRuleFieldValues) {
        return {};
    }
    // Seems like the repetition below should be easily eliminated, but although
    // it can be, the end solution doesn't end up being an improvement, because
    // the type-friendly DRY solution is more complicated and no less code.
    const overrides = {};
    if (overwritingRuleFieldValues[0] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoX"] = true;
    }
    else if (overwritingRuleFieldValues[0] !== undefined) {
        overrides["autoX"] = false;
        overrides.x = overwritingRuleFieldValues[0];
    }
    if (overwritingRuleFieldValues[1] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoY"] = true;
    }
    else if (overwritingRuleFieldValues[1] !== undefined) {
        overrides["autoY"] = false;
        overrides.y = overwritingRuleFieldValues[1];
    }
    if (overwritingRuleFieldValues[2] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoZ"] = true;
    }
    else if (overwritingRuleFieldValues[2] !== undefined) {
        overrides["autoZ"] = false;
        overrides.z = overwritingRuleFieldValues[2];
    }
    return overrides;
}
function clampVectorFields(vector = {}, minFieldValues, maxFieldValues) {
    if (minFieldValues) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        minFieldValues.forEach((minFieldValue) => {
            if (minFieldValue === undefined) {
                return;
            }
            if (vector.x !== undefined) {
                vector.x = Math.max(minFieldValue.x, vector.x);
            }
            if (vector.y !== undefined) {
                vector.y = Math.max(minFieldValue.y, vector.y);
            }
            if (vector.z !== undefined) {
                vector.z = Math.max(minFieldValue.z, vector.z);
            }
        });
    }
    if (maxFieldValues) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        maxFieldValues.forEach((maxFieldValue) => {
            if (maxFieldValue === undefined) {
                return;
            }
            if (vector.x !== undefined) {
                vector.x = Math.min(maxFieldValue.x, vector.x);
            }
            if (vector.y !== undefined) {
                vector.y = Math.min(maxFieldValue.y, vector.y);
            }
            if (vector.z !== undefined) {
                vector.z = Math.min(maxFieldValue.z, vector.z);
            }
        });
    }
    return vector;
}
function evaluateSelectorLengthsList(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange) {
    const tokens = splitStringComponentsByWhitespace(value).map((token) => token.trim());
    const parsedTokens = tokens.map((token) => {
        let selector = "*";
        let value = "";
        const hasSelector = token.includes("(");
        if (hasSelector) {
            const bracketIndex = token.indexOf("(");
            selector = token.substring(0, bracketIndex);
            value = token.substring(bracketIndex + 1, token.length - 1);
        }
        else {
            value = token;
        }
        return {
            selector,
            value: Number(evaluateInputValue(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false)) || 0, // `||` is important here to catch NaN-- don't replace with `??`
        };
    });
    return parsedTokens;
}
function clampValueFields(value, minFieldValues, maxFieldValues) {
    if (typeof value === "number") {
        let result = value;
        if (Number.isNaN(result)) {
            // NaN is a number type, which means we can arrive here with an unusable 'number'. To avoid propagating
            // unusable NaN's forward to the system, preemptively default to zero. If min/max ranges are applicable,
            // the subsequent logic will clamp result as required
            result = 0;
        }
        if (minFieldValues) {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            minFieldValues.forEach((minFieldValue) => {
                // Attempting Math.max() between NaN and valid number will result in NaN, so skipping over NaN is
                // important here
                if (minFieldValue === undefined || Number.isNaN(minFieldValue)) {
                    return;
                }
                result = Math.max(minFieldValue, result);
            });
        }
        if (maxFieldValues) {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            maxFieldValues.forEach((maxFieldValue) => {
                // Attempting Math.min() between NaN and valid number will result in NaN, so skipping over NaN is
                // important here
                if (maxFieldValue === undefined || Number.isNaN(maxFieldValue)) {
                    return;
                }
                result = Math.min(maxFieldValue, value);
            });
        }
        return result;
    }
    else {
        return value;
    }
}
function getOverrideFields(fields, values) {
    const overrides = {};
    for (let idx = 0; idx < fields.length; idx++) {
        if (values?.[idx] !== undefined) {
            overrides[fields[idx]] = values[idx];
        }
    }
    return overrides;
}
function evaluateInputValue(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, evaluateExpressionsOnly) {
    let expression = value;
    let result;
    if (expression !== "") {
        if (!evaluateExpressionsOnly && defaultInputUnit && isNumeric(value)) {
            expression = `${value} ${defaultInputUnit}`;
        }
        try {
            const evaluation = evaluateMechanicalValue(expression, !evaluateExpressionsOnly, expressionEvaluationContext);
            // If the expression is just a unit on its own, like "mm", then the evaluation will succeed
            // and the following normaliziation will transform "mm" to a number `0.001`. A unit on it's own
            // would be considered an invalid expression, rather than evaluate to a decimal. This case
            // corresponds to `evaluation?.value === null` - throw an error to invoke default fallback
            // behavior
            if (typeof evaluation === "object" && "value" in evaluation && evaluation.value === null) {
                throw new Error();
            }
            result = normalizeEvaluatedExpression(evaluation, false, false, convertToUnit, defaultInputUnit);
        }
        catch {
            // It's possible for normalizeEvaluatedExpression(..) to throw an exception
            // in the case that value is a unit, eg value = "A"
            // Catching the error, allows fallback/default behavior to kick in from here
        }
    }
    if (result === undefined || result === null) {
        return undefined;
    }
    if (typeof result === "number") {
        if (Number.isNaN(result)) {
            // A NaN is a number type, so we could potentially arrive here with a NaN, which we'd then could attempt to clamp or format for
            // precision adjustment. Worse still, we could simply return a NaN which would then be forwarded to the system as an unusable
            // number. Doing this ensures that the return value is a value of number type that is directly usable in arithmetic
            return undefined;
        }
        if (precision !== undefined) {
            result = Number(result.toFixed(precision));
        }
        if (validRange) {
            result = clampBetween(result, validRange[0], validRange[1]);
        }
        return result;
    }
    return result;
}
export function getStackupConfigFromValue(stackupValue, hasLayout, ruleData, parseCustomValues = true) {
    let stackupConfig;
    if (!Number.isNaN(Number(stackupValue))) {
        stackupConfig = Object.values(stackups.standard).find((stackup) => Object.values(stackup.layers).filter((layerConfig) => isCopperLayer(layerConfig)).length ===
            Number(stackupValue));
    }
    else {
        if (stackupValue &&
            stackupValue.toString().toLocaleLowerCase().includes("custom") &&
            ruleData?.payloadAsBase64) {
            try {
                const stackups = JSON.parse(atob(ruleData.payloadAsBase64));
                stackupConfig = stackups[stackupValue];
                if (stackupConfig) {
                    // Cleanup:
                    // The stackup editor introduces "Middle" as an orientation for the layers, which does not have good
                    // support in the app right now. Until we do, let's remove any invalid orientations
                    // TODO: They way we are using layer.orientation is very prone to errors, we should look into
                    // changing it completely, or making it more robust
                    const validOrientations = Object.values(LayerOrientation);
                    // NOTE: Intentionally using Array.forEach here with a lint override:
                    //
                    // Benchmarks (Node v20.19.4, Nov 2025) showed negligible performance differences at realistic
                    // stackup sizes (2-16 layers), and in an isolation micro-benchmark forEach ran ~2-10% faster
                    // than for...of. The dominant cost here is Object.values() + includes(), so iteration style
                    // has little impact in this code path. We therefore opt out of @buildwithflux/no-array-foreach
                    // for this specific loop based on measured performance characteristics.
                    // See PR #18473 for full methodology and benchmark results.
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    Object.values(stackupConfig.layers).forEach((layer) => {
                        if (!validOrientations.includes(layer.orientation)) {
                            layer.orientation = undefined;
                        }
                    });
                    if (parseCustomValues) {
                        stackupConfig = parseCustomStackupValues(stackupConfig);
                    }
                }
            }
            catch {
                // Bad data, fall back to the default stackup
            }
        }
        else {
            // TODO: replace Object.values(..) with iteration over stackups, via for(..in..) loop, over raw object.
            // This is usually much faster than Object.values(..), about 2x as fast https://jsbench.me/v6lpuomea4/1
            Object.values(stackups).some((stackupCategory) => {
                stackupConfig = Object.values(stackupCategory).find((stackup) => stackup.key === stackupValue || stackup.label === stackupValue);
                return !!stackupConfig;
            });
        }
    }
    if (!stackupConfig) {
        const defaultKey = hasLayout ? DEFAULT_LAYOUT_STACKUP_KEY : DEFAULT_FOOTPRINT_STACKUP_KEY;
        stackupConfig = Object.values(stackups.standard).find((stackup) => stackup.key === defaultKey);
    }
    if (stackupConfig) {
        // Populate the default vias if they are missing
        // This might happen in older custom templates when 'vias' and 'holes' were not part of the stackup
        if (!Object.values(stackupConfig.vias ?? {}).length) {
            const layersCount = getCopperLayersCount(stackupConfig.layers);
            stackupConfig.vias = createDefaultViaConfigurationsMap(layersCount);
        }
        // Populate the default holes if they are missing
        if (!stackupConfig.holes?.length) {
            stackupConfig.holes = createDefaultHoles();
        }
    }
    return stackupConfig;
}
export function getStackupLayersFromValue(stackupValue, hasLayout, ruleData, parseCustomValues = true) {
    const stackupConfig = getStackupConfigFromValue(stackupValue, hasLayout, ruleData, parseCustomValues);
    return stackupConfig?.layers ?? {};
}
/**
 * Retrieves all via configurations from a stackup value, including auto-generated sub-sampled configs.
 *
 * For each user-created via configuration, this function generates all possible contiguous
 * sub-sampled configurations (e.g., from segments A,B,C,D creates A, AB, ABC, B, BC, BCD, etc.).
 * These sub-sampled configs are used internally by the via selection algorithm to find optimal
 * routing solutions but are filtered from UI display.
 *
 * @param stackupValue - The stackup identifier or configuration
 * @param ruleData - Optional PCB rule data for context
 * @returns Map of all via configurations including base configs and auto-generated sub-samples
 */
export function getStackupViasFromValue(stackupValue, ruleData) {
    const stackupConfig = getStackupConfigFromValue(stackupValue, true, ruleData);
    // Get the user-created vias from the stackup config
    const userCreatedVias = stackupConfig?.vias ?? {};
    // ...and then generate all sub-sampled configs for each user-created via
    const allVias = create(userCreatedVias, (draftVias) => {
        for (const viaConfig of Object.values(draftVias)) {
            if (isUserCreatedViaConfig(viaConfig)) {
                Object.assign(draftVias, keyBy(generateSubSampledConfigs(viaConfig), "uid"));
            }
        }
    });
    return allVias;
}
export function getStackupViaHoleConfigsFromValue(stackupValue, ruleData) {
    const stackupConfig = getStackupConfigFromValue(stackupValue, true, ruleData);
    return stackupConfig?.holes ?? [];
}
const lengthValues = ["thickness"];
function parseCustomStackupValues(stackupConfig) {
    // Convert any length values to numbers
    const layers = Object.entries(stackupConfig.layers).reduce((acc, [layerId, layerConfig]) => {
        acc[layerId] = {
            ...layerConfig,
            ...Object.entries(layerConfig).reduce((acc, [key, value]) => {
                if (lengthValues.includes(key)) {
                    acc[key] = getLengthValue(value);
                }
                return acc;
            }, {}),
        };
        return acc;
    }, {});
    return { ...stackupConfig, layers };
}
function getLengthValue(value) {
    if (typeof value === "number") {
        return value;
    }
    // Fallback to a sane value on error
    const fallback = 0;
    try {
        const length = evaluateMechanicalValue(value, true);
        return typeof length === "object" ? length.toNumber("m") : fallback;
    }
    catch {
        return fallback;
    }
}
//# sourceMappingURL=PcbLayoutRuleParser.js.map